import { useCallback, useMemo } from "react";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";

// These are the search params this hook is able to handle
const searchParamKeys = [
  // Show the modal for creating a new task.
  // Value is the task type id of the task to create.
  "taskCreator",

  // Show the modal for viewing a task.
  // Value is the id of the task to view.
  "taskViewer",

  // Show the modal for editing a task.
  // Value is the id of the task to edit.
  "taskEditor",

  // Show the modal for creating a new task with pre-filled values.
  // Value is a JSON stringified object with the initial values for the task.
  "initialValues",

  "taskDetails",
  "tab",
] as const;

type SearchParamStateKey = (typeof searchParamKeys)[number];

// Typed wrapper for URLSearchParams, only allows SearchParamStateKey keys
class TypedSearchParams {
  private params: URLSearchParams;
  constructor(params: URLSearchParams) {
    this.params = params;
  }
  set(key: SearchParamStateKey, value: string) {
    this.params.set(key, value);
  }
  delete(key: SearchParamStateKey) {
    this.params.delete(key);
  }
  toString() {
    return this.params.toString();
  }
}

type SearchParamStateValues = { [K in SearchParamStateKey]: string | null };

/**
 * Holds state in the url search params.
 *
 * Example: /some/path?createTask=1
 *
 * useSearchParamState()
 * returns
 * [ searchParams, setSearchParams ]
 * where
 * searchParams
 * is
 * { taskCreator: 1, taskViewer: null }
 *
 * Calling
 * setSearchParamsState((prev) => {
 *   prev.delete("taskCreator");
 *   prev.set("taskViewer", 2);
 * })
 * navigates to
 * /some/path?taskViewer=2
 */
function useSearchParamState(): [
  SearchParamStateValues,
  (updater: (params: TypedSearchParams) => void) => void,
] {
  const navigate = useFixaNavigate();
  const { pathname, search } = useFixaLocation();

  const params = useMemo<SearchParamStateValues>(() => {
    const urlParams = new URLSearchParams(search);
    return Object.fromEntries(
      searchParamKeys.map((key) => [key, urlParams.get(key)])
    ) as SearchParamStateValues;
  }, [search]);

  const setParams = useCallback(
    (updater: (prev: TypedSearchParams) => void) => {
      const urlParams = new TypedSearchParams(new URLSearchParams(search));
      updater(urlParams);
      const paramString = urlParams.toString();
      navigate(`${pathname}${paramString ? `?${paramString}` : ""}`);
    },
    [navigate, pathname, search]
  );

  return [params, setParams];
}

export default useSearchParamState;
